<template>
  <section v-if="rows.length" class="ticket-owners-section">
    <div class="ticket-owners-heading">
      <h2>{{ $t('ticketOwnerDetails') }}</h2>
      <p>{{ $t('ticketOwnerDetailsDescription') }}</p>
    </div>

    <div class="ticket-owner-list">
      <article
        v-for="(row, index) in rows"
        :key="`${row.ticketId}-${index}`"
        class="ticket-owner-card"
      >
        <button type="button" class="ticket-owner-header" @click="toggle(index)">
          <span class="material-icons ticket-owner-icon">confirmation_number</span>
          <span class="ticket-owner-title">{{ ticketTitle(row, index) }}</span>
          <span class="material-icons ticket-owner-arrow">
            {{ expandedIndex === index ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </span>
        </button>

        <div v-show="expandedIndex === index" class="ticket-owner-body">
          <label class="owner-checkbox-row">
            <input
              v-model="row.sameAsBuyer"
              type="checkbox"
              class="owner-checkbox"
              @change="onSameAsBuyerChange(row, index)"
            >
            <span>{{ $t('ticketOwnerSameAsBuyer') }}</span>
          </label>

          <div class="owner-fields">
            <text-field
              v-model.trim="row.firstName"
              :label="`${$t('firstName')}*`"
              :disabled="row.sameAsBuyer"
              :validator="ownerValidator(index, 'firstName')"
              :error-messages="{ required: $t('ticketOwnerFirstNameRequired') }"
              box
              @input="emitRows"
            />
            <text-field
              v-model.trim="row.lastName"
              :label="`${$t('lastName')}*`"
              :disabled="row.sameAsBuyer"
              :validator="ownerValidator(index, 'lastName')"
              :error-messages="{ required: $t('ticketOwnerLastNameRequired') }"
              box
              @input="emitRows"
            />
          </div>

          <div class="owner-email-row">
            <label
              :class="[
                'owner-checkbox-row',
                isSendTicketToOwnerDisabled(row) && 'owner-checkbox-row-disabled',
              ]"
            >
              <input
                v-model="row.sendTicketToOwner"
                type="checkbox"
                class="owner-checkbox"
                :disabled="isSendTicketToOwnerDisabled(row)"
                @change="onSendTicketChange(row)"
              >
              <span>{{ $t('ticketOwnerSendTicketToOwner') }}</span>
            </label>

            <text-field
              v-model.trim="row.email"
              :label="`${$t('email')}*`"
              :disabled="row.sameAsBuyer"
              :validator="ownerValidator(index, 'email')"
              :error-messages="{
                required: $t('ticketOwnerEmailRequired'),
                email: $t('EmailError'),
              }"
              box
              class="owner-email-input"
              @input="onEmailInput(row)"
            />
          </div>

          <div v-if="fields.length" class="owner-extra-fields">
            <component
              :is="field.component"
              v-for="field in fields"
              :key="field.key"
              v-model="row[field.key]"
              :validator="ownerValidator(index, field.key)"
              v-bind="field"
              :error-messages="{
                ...field.errorMessages,
              }"
              @input="emitRows"
            />
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
const normalizeEmail = value => (value || '').toString().trim().toLowerCase()

export default {
  name: 'ticket-owners-accordion',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    tickets: {
      type: Array,
      default: () => [],
    },
    buyer: {
      type: Object,
      default: () => ({}),
    },
    validation: {
      type: Object,
      default: null,
    },
    validationAttempt: {
      type: Number,
      default: 0,
    },
    fields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      rows: [],
      expandedIndex: 0,
      syncing: false,
    }
  },
  watch: {
    tickets: {
      handler() {
        this.syncRows()
      },
      immediate: true,
      deep: true,
    },
    fields: {
      handler() {
        this.syncRows()
      },
      deep: true,
    },
    value: {
      handler() {
        if (!this.syncing) {
          this.syncRows()
        }
      },
      deep: true,
    },
    buyer: {
      handler() {
        this.rows.forEach(row => {
          if (row.sameAsBuyer) {
            this.copyBuyerData(row)
          }
          this.applySendDefault(row)
        })
        this.emitRows()
      },
      deep: true,
    },
    validationAttempt() {
      this.openFirstInvalid()
    },
  },
  methods: {
    syncRows() {
      const rows = this.tickets.map((ticket, index) => {
        const existing = this.value[index] || this.rows[index] || {}
        const row = {
          ticketId: existing.ticketId || ticket.id || ticket.ticketId,
          ticketName: existing.ticketName || ticket.name || ticket.description || '',
          firstName: existing.firstName || '',
          lastName: existing.lastName || '',
          email: existing.email || '',
          sameAsBuyer:
            typeof existing.sameAsBuyer === 'boolean' ? existing.sameAsBuyer : index === 0,
          sendTicketToOwner: existing.sendTicketToOwner === true,
          sendTicketToOwnerTouched: existing.sendTicketToOwnerTouched === true,
        }

        this.fields.forEach(field => {
          this.$set(row, field.key, existing[field.key])
        })

        if (row.sameAsBuyer) {
          this.copyBuyerData(row)
        }
        this.applySendDefault(row)

        return row
      })

      this.rows = this.normalizeSameAsBuyerRows(rows)
      if (this.expandedIndex >= rows.length) {
        this.expandedIndex = rows.length ? rows.length - 1 : 0
      }
      this.emitRows()
    },
    normalizeSameAsBuyerRows(rows) {
      let buyerRowFound = false

      return rows.map(row => {
        if (!row.sameAsBuyer) {
          return row
        }

        if (buyerRowFound) {
          this.clearBuyerData(row)

          return {
            ...row,
            sameAsBuyer: false,
          }
        }

        buyerRowFound = true

        return row
      })
    },
    buyerValue(...keys) {
      for (const key of keys) {
        if (this.buyer && this.buyer[key]) {
          return this.buyer[key]
        }
      }

      return ''
    },
    copyBuyerData(row) {
      row.firstName = this.buyerValue('First name', 'firstName')
      row.lastName = this.buyerValue('Last name', 'lastName')
      row.email = this.buyerValue('Email', 'email')
    },
    clearBuyerData(row) {
      if (row.firstName === this.buyerValue('First name', 'firstName')) {
        row.firstName = ''
      }
      if (row.lastName === this.buyerValue('Last name', 'lastName')) {
        row.lastName = ''
      }
      if (normalizeEmail(row.email) === normalizeEmail(this.buyerValue('Email', 'email'))) {
        row.email = ''
      }
    },
    toggle(index) {
      this.expandedIndex = this.expandedIndex === index ? -1 : index
    },
    ticketTitle(row, index) {
      if (row.ticketName) {
        return this.$t('ticketOwnerTitleWithName', {
          ticketName: row.ticketName,
          ticketNumber: index + 1,
        })
      }

      return this.$t('ticketOwnerTitle', { ticketNumber: index + 1 })
    },
    isBuyerEmail(email) {
      const buyerEmail = normalizeEmail(this.buyerValue('Email', 'email'))

      return !!buyerEmail && normalizeEmail(email) === buyerEmail
    },
    isSendTicketToOwnerDisabled(row) {
      return !!row.sameAsBuyer
    },
    applySendDefault(row) {
      if (this.isSendTicketToOwnerDisabled(row)) {
        row.sendTicketToOwner = false

        return
      }
      if (!row.sendTicketToOwnerTouched && row.email) {
        row.sendTicketToOwner = true
      }
    },
    onSameAsBuyerChange(row, index) {
      if (row.sameAsBuyer) {
        this.rows.forEach((otherRow, otherIndex) => {
          if (otherIndex !== index && otherRow.sameAsBuyer) {
            otherRow.sameAsBuyer = false
            this.clearBuyerData(otherRow)
            this.applySendDefault(otherRow)
          }
        })
        this.copyBuyerData(row)
      }
      this.applySendDefault(row)
      this.emitRows()
    },
    onSendTicketChange(row) {
      row.sendTicketToOwnerTouched = true
      this.emitRows()
    },
    onEmailInput(row) {
      this.applySendDefault(row)
      this.emitRows()
    },
    ownerValidator(index, key) {
      return this.validation && this.validation.$each && this.validation.$each[index]
        ? this.validation.$each[index][key]
        : null
    },
    openFirstInvalid() {
      if (!this.validation || !this.validation.$each) {
        return
      }

      const firstInvalidIndex = this.rows.findIndex((_, index) => {
        const rowValidation = this.validation.$each[index]

        return rowValidation && rowValidation.$invalid
      })

      if (firstInvalidIndex !== -1) {
        this.expandedIndex = firstInvalidIndex
      }
    },
    emitRows() {
      this.syncing = true
      this.$emit(
        'input',
        this.rows.map(row => ({
          ticketId: row.ticketId,
          ticketName: row.ticketName,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
          ...this.getAttendeeFieldValues(row),
          sameAsBuyer: row.sameAsBuyer,
          sendTicketToOwner: !this.isSendTicketToOwnerDisabled(row) && row.sendTicketToOwner,
          sendTicketToOwnerTouched: row.sendTicketToOwnerTouched,
        })),
      )
      this.$nextTick(() => {
        this.syncing = false
      })
    },
    getAttendeeFieldValues(row) {
      return this.fields.reduce((acc, field) => {
        acc[field.key] = row[field.key]

        return acc
      }, {})
    },
  },
}
</script>

<style scoped>
.ticket-owners-section {
  border-top: 1px solid rgba(151, 151, 151, 0.5);
  margin: 0 -24px;
  padding: 32px 24px 40px;
}

.ticket-owners-heading h2 {
  color: #333;
  font-family: Raleway, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.72px;
  margin: 0 0 8px;
}

.ticket-owners-heading p {
  color: #333;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 150%;
  margin: 0 0 24px;
}

.ticket-owner-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ticket-owner-card {
  border: 1px solid rgba(151, 151, 151, 0.5);
  border-radius: 4px;
  overflow: hidden;
}

.ticket-owner-header {
  align-items: center;
  background: #f5f5f5;
  border: 0;
  border-bottom: 1px solid rgba(151, 151, 151, 0.5);
  border-radius: 0;
  cursor: pointer;
  display: flex;
  gap: 24px;
  margin: 0;
  min-height: 56px;
  padding: 16px 14px;
  text-align: left;
  width: 100%;
}

.ticket-owner-icon {
  color: #666;
  font-size: 24px;
}

.ticket-owner-title {
  color: #333;
  flex: 1;
  font-family: Raleway, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.6px;
}

.ticket-owner-arrow {
  color: #333;
  font-size: 24px;
}

.ticket-owner-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px 24px;
}

.owner-checkbox-row {
  align-items: center;
  color: #333;
  display: flex;
  font-family: Raleway, sans-serif;
  font-size: 16px;
  font-weight: 500;
  gap: 16px;
  letter-spacing: 0.24px;
  line-height: 120%;
}

.owner-checkbox-row-disabled {
  color: #979797;
}

.owner-checkbox {
  accent-color: #0277bd;
  height: 16px;
  width: 16px;
}

.owner-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.owner-email-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.owner-email-input {
  flex: 1;
  min-width: 220px;
}

.owner-extra-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media screen and (max-width: 700px) {
  .ticket-owner-header {
    gap: 12px;
  }

  .ticket-owner-title {
    font-size: 18px;
  }

  .ticket-owner-body {
    padding: 24px 16px;
  }
}
</style>
